
import {TripbooksContainer} from "../components/TripbooksContainer.tsx";
import {AgencyDetailContainer} from "../components/AgencyDetailContainer.tsx";
import React, {useEffect, useState} from "react";
import * as Sentry from "@sentry/react";
import {useTranslation} from "react-i18next";
import {DashboardSidebar} from "../components/DashboardSidebar.tsx";
import {onAuthStateChanged} from "firebase/auth";
import {auth} from "../firebase.ts";
import {setUser} from "../features/auth.ts";
import {useDispatch, useSelector} from "react-redux";
import {WhoAmIUseCase} from "../domain/useCases/auth/WhoAmIUseCase.ts";
import {PartnerRepository} from "../repositories/PartnerRepository.ts";
import {useNavigate} from "react-router-dom";
import {LogoutUseCase} from "51trips-frontend-core";
import {AuthenticationRepository} from "../repositories/AuthenticationRepository.ts";
import {getBreakpoint} from "../utils/getBreakpoint.ts";
import {MobileTabletPage} from "./MobileTabletPage.tsx";
import {DashboardOverview} from "../components/dashboardOverview/DashboardOverview.tsx";
import {AgenciesTable} from "../components/agenciesTable/AgenciesTable.tsx";
import {MyPacksTripbooksContainer} from "../components/MyPacksTripbooksContainer.tsx";
import {MyAgencyOverviewContainer} from "../components/MyAgencyOverviewContainer.tsx";
import {MyReferralsContainer} from "../components/MyReferralsContainer.tsx";
import {AdminCodesContainer} from "../components/adminCodes/AdminCodesContainer.tsx";

// Lazy: the mockup it renders embeds ~13MB of base64 template images, kept
// out of the main bundle so non-admin users never download it.
import {useAgencyKitNotifier} from "../components/agencyKits/useAgencyKitNotifier.tsx";

const MarketingMockupsContainer = React.lazy(() =>
    import("../components/marketingMockups/MarketingMockupsContainer.tsx")
        .then(module => ({default: module.MarketingMockupsContainer}))
)
// Lazy for the same reason: it runs the same mockup engine in a hidden iframe.
const AgencyKitsContainer = React.lazy(() =>
    import("../components/agencyKits/AgencyKitsContainer.tsx")
        .then(module => ({default: module.AgencyKitsContainer}))
)

export const DashboardPage: React.FC = () => {

    const {t} = useTranslation('dashboard')
    const whoAmIUseCase = new WhoAmIUseCase(new PartnerRepository())
    const logoutUseCase = new LogoutUseCase(new AuthenticationRepository())
    const url = window.location.pathname
    const [path, setPath] = React.useState(url.split('/').slice(2).join('/') || 'tripbooks')
    const navigate = useNavigate()
    const breakpoint = getBreakpoint()
    const user = useSelector((state) => state.auth.user)
    const authenticatedUser = user as any
    const isAdmin = authenticatedUser?.role === 'admin'
    const dispatch = useDispatch()

    const [partnerData, setPartnerData] = React.useState(null)
    const [displayCreateTripItineraryDialog, setDisplayCreateTripItineraryDialog] = useState<boolean>(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    const forceLogoutForMissingAccess = () => {
        logoutUseCase.logout().subscribe({
            next: () => navigate('/auth/login', {state: {authError: 'noAccess'}}),
            error: () => navigate('/auth/login', {state: {authError: 'noAccess'}})
        })
    }

    useEffect(() => {
        onAuthStateChanged(auth, async (user) => {
            if (user) {
                const idTokenResult = await user.getIdTokenResult();
                const role = idTokenResult.claims.role;
                const agencyId = idTokenResult.claims.agencyId;
                const tourOperatorId = idTokenResult.claims.tourOperatorId;
                const partnerId = tourOperatorId || agencyId || (role === 'admin' ? user.uid : undefined)

                if (!role || !partnerId) {
                    forceLogoutForMissingAccess()
                    return
                }

                whoAmIUseCase.whoAmI(role, partnerId).then((partner) => {
                    dispatch(setUser({
                        ...partner,
                        email: user.email ?? undefined,
                        displayName: user.displayName ?? undefined,
                    }))
                    setPartnerData(partner)
                }).catch(() => {
                    forceLogoutForMissingAccess()
                })
            }
        })
    }, []);

    const setPathView = (path: string) => {
        setPath(path)
        window.history.pushState({}, '', `/dashboard${path === 'dashboard' ? '' : '/' + path}`)
    }

    // "Kit ready" notice for kits generated from Kit comercial, in any section.
    useAgencyKitNotifier(() => setPathView('agency-kits'))

    const renderContent = () => {
        if (path === 'overview') {
            if (isAdmin) return <DashboardOverview setPath={setPathView}/>;
            if (authenticatedUser?.role === 'agency') return <MyAgencyOverviewContainer agencyId={authenticatedUser.id} setPath={setPathView}/>;
            navigate('/dashboard'); return;
        }

        if (path === 'referrals') {
            if (authenticatedUser?.role !== 'agency') { navigate('/dashboard'); return; }
            return <MyReferralsContainer agencyId={authenticatedUser.id}/>;
        }

        if (path === 'agencies') {
            if (!isAdmin) { navigate('/dashboard'); return; }
            return <AgenciesTable setPath={setPathView}/>;
        }

        if (path === 'codes-monitor') {
            if (!isAdmin) { navigate('/dashboard'); return; }
            return <AdminCodesContainer/>;
        }

        if (path === 'marketing-mockups') {
            if (!isAdmin) { navigate('/dashboard'); return; }
            return (
                <React.Suspense fallback={<div/>}>
                    <MarketingMockupsContainer/>
                </React.Suspense>
            );
        }

        if (path === 'agency-kits') {
            if (!isAdmin) { navigate('/dashboard'); return; }
            return (
                <React.Suspense fallback={<div/>}>
                    <AgencyKitsContainer/>
                </React.Suspense>
            );
        }

        if (path.startsWith('agency/')) {
            if (!isAdmin) { navigate('/dashboard'); return; }
            const agencyId = path.split('/')[1]
            return (
                <div className="h-full relative">
                    <AgencyDetailContainer agencyId={agencyId} setPath={setPathView}/>
                </div>
            );
        }

        if (authenticatedUser?.role === 'agency') {
            return <MyPacksTripbooksContainer agencyId={authenticatedUser.id}/>;
        }

        return (
            <TripbooksContainer displayCreateTripItineraryDialog={(bool) => {
                setDisplayCreateTripItineraryDialog(bool)
            }} setPath={setPathView}/>
        );
    }

    if (!partnerData || !user) return <div></div>

    // Only the agency self-service view has a responsive mobile layout so far
    // (see "Mobile - (Vista agencia) Codigo unico" design) — admin/tourOperator
    // views still fall back to the desktop-only placeholder below 1024px.
    if (breakpoint.isNotDesktop && authenticatedUser?.role !== 'agency') return <MobileTabletPage/>

    const activeSection = path.startsWith('agency/') ? 'agencies' : path;

    return (
        <div className="flex flex-row max-[860px]:flex-col bg-neutralBlack-100 w-full h-screen overflow-hidden">
            <DashboardSidebar
                partnerData={partnerData}
                currentUserDisplayName={authenticatedUser?.displayName}
                currentUserEmail={authenticatedUser?.email}
                isAdmin={isAdmin}
                activeSection={activeSection}
                collapsed={sidebarCollapsed}
                onToggleCollapse={() => setSidebarCollapsed(c => !c)}
                setPathView={setPathView}
            />

            {/* Main content */}
            <div className="flex-1 min-w-0 min-h-0 p-5 max-[860px]:p-4 overflow-hidden">
                {/* Scoped to the content area, not the whole page: a crash in one
                    section (e.g. an unhandled API error) shouldn't blank out the
                    sidebar too — the root errorElement (ErrorPage) is the last
                    resort for everything else. `key={path}` remounts the boundary
                    on navigation, so switching sections clears a previous crash
                    without needing an explicit reset. */}
                <Sentry.ErrorBoundary
                    key={path}
                    fallback={({resetError}) => (
                        <div className="h-full flex flex-col items-center justify-center gap-3 text-center px-6">
                            <span className="text-[15px] font-semibold text-neutralBlack-900">{t('dashboardErrorTitle')}</span>
                            <p className="text-[13.5px] text-neutralBlack-500 max-w-sm">{t('dashboardErrorMessage')}</p>
                            <div className="flex items-center gap-2.5 mt-1">
                                <button type="button" onClick={resetError}
                                        className="h-9 px-4 rounded-full bg-neutralBlack-900 text-white text-[13px] font-medium hover:bg-neutralBlack-700">
                                    {t('dashboardErrorRetry')}
                                </button>
                                <button type="button" onClick={() => setPathView('tripbooks')}
                                        className="h-9 px-4 rounded-full border border-neutralBlack-200 bg-white text-[13px] text-neutralBlack-700 hover:bg-neutralBlack-50">
                                    {t('dashboardErrorGoHome')}
                                </button>
                            </div>
                        </div>
                    )}
                >
                    {renderContent()}
                </Sentry.ErrorBoundary>
            </div>
        </div>
    )
}
